/**
 * Org Service — contratos compartidos back/front (tipos + catálogo de soluciones).
 *
 * Es el servicio de permisos y organigrama de Winclap: responde quién es cada persona,
 * en qué solución trabaja, con qué clientes y (desde T5) qué accesos le corresponden.
 * Plan: doc "Plan: Servicio de permisos y organigrama" (26/10/2026).
 *
 * El sistema de registro es RELACIONAL: el schema `org` de Postgres en el Aurora del
 * Brain (RDS Data API). Cada `interface Org…Row` de acá es UNA tabla de ese schema, con
 * los mismos nombres de columna (snake_case), para que el modelo se lea igual en SQL, en
 * TypeScript y en la copia `org_*` de BigQuery. El DDL vive en
 * `backend/src/services/org/schema.ts`; `npm run test:org` (CI) verifica que cada columna
 * de cada Row exista en el DDL, así los dos no se desincronizan.
 *
 * Fuentes de verdad (se referencian, no se copian):
 *  - Persona: el Community Map. `CommunityMember.employeeId` es el mismo número que
 *    `team_members.employee_id` de la app de Carga de Horas (verificado el 26/10/2026:
 *    298 de 323 cruzan, 0 activos sin cruce).
 *  - Cliente: `shared/brain-clients/*.json` (el clientId del Brain).
 *  - Asignación persona × cliente × solución × mes: la app de Carga de Horas y Assets
 *    (BigQuery `investment-framework.neondb_hourAllocator`, us-central1) para las
 *    soluciones con `source: 'allocator'`; el panel/chat del manager para el resto.
 *
 * Las reglas puras (vigencia, estado derivado, resolución de nombres, higiene) viven en
 * `shared/org-rules.ts`. Acá solo hay datos y tipos.
 */

// ─── Enumeraciones ───────────────────────────────────────────────────────────

/** De dónde salió la asignación. `capacity` = la app de Carga de Horas (sync diario). */
export const ORG_ASSIGNMENT_ORIGINS = ['capacity', 'declared', 'inferred'] as const;
export type OrgAssignmentOrigin = (typeof ORG_ASSIGNMENT_ORIGINS)[number];

/**
 * `suggested`: la vio el sistema, nadie la confirmó · `confirmed`: el manager la confirmó
 * (en la app = lock del cliente-mes; en Internal = clic) · `closed`: dejó de estar vigente.
 */
export const ORG_ASSIGNMENT_STATUSES = ['suggested', 'confirmed', 'closed'] as const;
export type OrgAssignmentStatus = (typeof ORG_ASSIGNMENT_STATUSES)[number];

/** Por qué se cerró una asignación. */
export const ORG_CLOSE_REASONS = ['removed_from_source', 'manager', 'offboarding', 'period_ended'] as const;
export type OrgCloseReason = (typeof ORG_CLOSE_REASONS)[number];

export const ORG_ACCESS_STATUSES = ['requested', 'granted', 'revoked', 'dangling'] as const;
export type OrgAccessStatus = (typeof ORG_ACCESS_STATUSES)[number];

export const ORG_TOOL_MODALITIES = ['individual', 'shared', 'client_invite', 'partner'] as const;
export type OrgToolModality = (typeof ORG_TOOL_MODALITIES)[number];

export const ORG_POLICY_MOMENTS = ['day1', 'on_assignment'] as const;
export type OrgPolicyMoment = (typeof ORG_POLICY_MOMENTS)[number];

/** De dónde salen las asignaciones de una solución. `none` = área sin clientes (patrón 2). */
export const ORG_SOLUTION_SOURCES = ['allocator', 'chat', 'none'] as const;
export type OrgSolutionSource = (typeof ORG_SOLUTION_SOURCES)[number];

/**
 * Patrón de acceso de la solución (tabla "Arquitectura" del plan):
 *  1 = por cliente: la asignación persona × cliente abre los recursos del cliente;
 *  2 = por área / solución: núcleo automático al entrar a la solución, sin clientes;
 *  3 = función técnica: lista explícita que aprueba IT;
 *  4 = elevado: IT, con revisión trimestral.
 */
export type OrgAccessPattern = 1 | 2 | 3 | 4;

// ─── Catálogo de soluciones ──────────────────────────────────────────────────

export interface OrgSolution {
  /** Clave estable (`org.solution.slug`). */
  slug: string;
  name: string;
  /** `solutions.name` en la app de Carga de Horas; null si no carga ahí. */
  allocatorName: string | null;
  /** `PracticeName` en `Capacity_Final`; null si no factura como practice. */
  capacityPractice: string | null;
  /** Áreas del Community Map (columna Área) que componen la solución, por nombre exacto. */
  communityAreas: readonly string[];
  /**
   * Patrones (RegExp, sobre el área sin tildes y en minúsculas) que caen en la solución
   * cuando el nombre no es exacto. Misma idea que `AREA_GROUP_RULES` de
   * backend/src/lib/cognito-area-sync.ts (área → grupo de Cognito): son dos mapas con
   * destinos distintos; si People renombra un área hay que tocar los dos.
   */
  areaPatterns: readonly string[];
  accessPattern: OrgAccessPattern;
  /** true = trabaja por cliente (lleva asignaciones); false = área (patrón 2). */
  worksWithClients: boolean;
  source: OrgSolutionSource;
}

/**
 * Semilla de `org.solution`. Cobertura medida el 26/10/2026 sobre septiembre 2026
 * (personas con cliente real en la app / activos del área en el Community Map):
 * Creative Studio 95/164, Paid Media Lab 51/67, Owned Media Lab 22/26, Tech & Data
 * Studio 4/7, Growth Strategy 2/5. Partnerships viene como bolsas `CAPACITY - *` sin
 * persona y Business (growth partners) no carga: van por chat.
 */
export const ORG_SOLUTIONS: readonly OrgSolution[] = [
  { slug: 'creative-studio', name: 'Creative Studio', allocatorName: 'Content', capacityPractice: 'Content', communityAreas: ['Creative Studio'], areaPatterns: ['creative'], accessPattern: 1, worksWithClients: true, source: 'allocator' },
  { slug: 'paid-media', name: 'Paid Media Lab', allocatorName: 'Paid Media Lab', capacityPractice: 'Paid Media Lab', communityAreas: ['Paid Media Lab'], areaPatterns: ['paid media'], accessPattern: 1, worksWithClients: true, source: 'allocator' },
  { slug: 'owned-media', name: 'Owned Media Lab', allocatorName: 'Owned Media Lab', capacityPractice: 'Owned Media Lab', communityAreas: ['Owned Media Lab'], areaPatterns: ['owned media'], accessPattern: 1, worksWithClients: true, source: 'allocator' },
  { slug: 'tech-data-studio', name: 'Tech & Data Studio (Martech + Measurement)', allocatorName: 'Tech & Data Studio', capacityPractice: 'Tech & Data Studio', communityAreas: ['Data'], areaPatterns: ['data'], accessPattern: 1, worksWithClients: true, source: 'allocator' },
  { slug: 'growth-strategy', name: 'Growth Strategy', allocatorName: 'Growth Strategy', capacityPractice: 'Growth Strategy', communityAreas: ['Growth Strategy'], areaPatterns: ['growth'], accessPattern: 1, worksWithClients: true, source: 'allocator' },
  { slug: 'branding', name: 'Branding', allocatorName: 'Branding', capacityPractice: 'Branding', communityAreas: [], areaPatterns: ['branding'], accessPattern: 1, worksWithClients: true, source: 'allocator' },
  { slug: 'other-income', name: 'Other Income', allocatorName: 'Other Income', capacityPractice: 'Other Income', communityAreas: [], areaPatterns: [], accessPattern: 1, worksWithClients: true, source: 'allocator' },
  { slug: 'partnerships', name: 'Partnerships', allocatorName: 'Solution Partners', capacityPractice: 'Solution Partners', communityAreas: ['Partnerships & Marketing'], areaPatterns: ['partnership'], accessPattern: 1, worksWithClients: true, source: 'chat' },
  { slug: 'business', name: 'Business (growth partners)', allocatorName: null, capacityPractice: null, communityAreas: ['Business SPLA', 'Business US', 'Business BR'], areaPatterns: ['business'], accessPattern: 1, worksWithClients: true, source: 'chat' },
  { slug: 'technology', name: 'Technology', allocatorName: null, capacityPractice: null, communityAreas: ['Technology', 'Tech & Product'], areaPatterns: ['tech'], accessPattern: 2, worksWithClients: false, source: 'none' },
  { slug: 'people', name: 'People', allocatorName: null, capacityPractice: null, communityAreas: ['People'], areaPatterns: ['people'], accessPattern: 2, worksWithClients: false, source: 'none' },
  { slug: 'finance', name: 'Finance, Strategy & Planning', allocatorName: null, capacityPractice: null, communityAreas: ['Finance, Strategy & Planning'], areaPatterns: ['finance'], accessPattern: 2, worksWithClients: false, source: 'none' },
  { slug: 'executive', name: 'Executive', allocatorName: null, capacityPractice: null, communityAreas: ['Executive'], areaPatterns: ['executive'], accessPattern: 2, worksWithClients: false, source: 'none' },
];

// ─── Filas (una interface = una tabla del schema `org`) ──────────────────────

/** `org.solution`: catálogo curado (semilla = ORG_SOLUTIONS). */
export interface OrgSolutionRow {
  slug: string;
  name: string;
  allocator_name: string | null;
  capacity_practice: string | null;
  community_areas: string[];
  access_pattern: OrgAccessPattern;
  works_with_clients: boolean;
  source: OrgSolutionSource;
  owner_email: string | null;
  updated_at: string;
}

/** `org.person`: la referencia a la persona del Community Map (clave = employeeId). Nada de costos. */
export interface OrgPersonRow {
  employee_id: number;
  email: string;
  full_name: string;
  role_title: string | null;
  level: string | null;
  area: string | null;
  team: string | null;
  /** Solución principal, derivada del área del Community Map. */
  solution_slug: string | null;
  people_manager_email: string | null;
  /** Activo | Inactivo | Baja (tal cual el Community Map). */
  status: string;
  /** `team_members.id` en la app de Carga de Horas, si la persona existe ahí. */
  allocator_member_id: string | null;
  allocator_active: boolean | null;
  synced_at: string;
}

/** `org.client`: la referencia al cliente del Brain + lo que la app dice del cliente. */
export interface OrgClientRow {
  client_id: string;
  display_name: string;
  capacity_names: string[];
  allocator_names: string[];
  /** Pod / geo de `client_extras` (SoCo, Mexico, Andinos, Brazil, MELI, US, KC…). */
  pod: string | null;
  lead_email: string | null;
  n_plus_one_email: string | null;
  growth_partner_email: string | null;
  synced_at: string;
}

/**
 * `org.client_lock`: el cliente-mes está cerrado (lock) por el manager en la fuente. Es lo que
 * confirma las asignaciones de ese mes y lo que decide la vigencia del mes anterior
 * (`isAssignmentCurrent`). Un cliente del Brain cuenta como lockeado cuando TODOS los nombres
 * de la app que resuelven a él están lockeados.
 */
export interface OrgClientLockRow {
  client_id: string;
  period: string;
  locked_by: string | null;
  source_system: string;
  synced_at: string;
}

/** `org.client_alias_pending`: nombres de cliente vistos en una fuente que no resuelven a un clientId (para darlos de alta). */
export interface OrgClientAliasPendingRow {
  source: string;
  name: string;
  first_seen: string;
  last_seen: string;
  persons: number;
  updated_at: string;
}

/** `org.assignment`: persona × cliente × solución × período. La entidad central. */
export interface OrgAssignmentRow {
  /** Determinístico: `<employee_id>:<client_id>:<solution_slug>:<period>` (`assignmentId`). */
  id: string;
  employee_id: number;
  client_id: string;
  solution_slug: string;
  /** 'YYYY-MM'. */
  period: string;
  /** Puesto en el momento (`role_at_time` de la app o puesto del Community Map). */
  role_title: string | null;
  /** Dedicación planificada 0–100. Es capacity, NO acceso: un 5 % abre el cliente igual. */
  pct: number | null;
  origin: OrgAssignmentOrigin;
  status: OrgAssignmentStatus;
  /** Quién confirma: el manager vigente de la persona en ese período. */
  manager_email: string | null;
  confirmed_by: string | null;
  confirmed_at: string | null;
  closed_at: string | null;
  closed_reason: OrgCloseReason | null;
  /** 'hour-allocator' | 'internal' | 'slack' | 'catalyst'… */
  source_system: string | null;
  /** Id de la fila de origen (`allocations.id`), para auditar. */
  source_ref: string | null;
  /** Nombre del cliente tal cual lo escribió la fuente (antes de resolverlo a client_id). */
  source_client_name: string | null;
  created_at: string;
  updated_at: string;
}

/** Lo que escribe el sync o el panel en `org.assignment` (la base pone created_at / updated_at). */
export type OrgAssignmentUpsert = Omit<OrgAssignmentRow, 'created_at' | 'updated_at'>;

/** `org.assignment_history`: append-only; una fila por cambio de campo o de estado. */
export interface OrgAssignmentHistoryRow {
  id: number;
  assignment_id: string;
  at: string;
  /** Email de quien lo hizo, o 'sync:hour-allocator'. */
  by: string;
  /** created | updated | confirmed | closed | reopened */
  action: string;
  field: string | null;
  from_value: string | null;
  to_value: string | null;
}

/** Un evento de historial antes de insertarse (la base pone id y at). */
export type OrgHistoryEvent = Omit<OrgAssignmentHistoryRow, 'id' | 'at'>;

export interface OrgSyncStats {
  persons: number;
  clients: number;
  assignments: number;
  created: number;
  updated: number;
  confirmed: number;
  closed: number;
  unresolved_clients: number;
  unmatched_persons: number;
}

export type OrgHygieneKind =
  | 'employee_id_reused'
  | 'active_in_app_but_baja'
  | 'person_not_in_community'
  | 'manager_not_in_community'
  | 'client_unresolved'
  | 'area_unmapped'
  // T5: evidencia desde el Vault
  | 'secret_without_tool'
  | 'secret_client_unresolved'
  | 'grant_email_unknown'
  | 'vault_evidence_partial'
  | 'sync_failed';

export interface OrgHygieneIssue {
  kind: OrgHygieneKind;
  /** Texto corto sin datos sensibles (nombres de cliente sí; costos nunca). */
  detail: string;
  count?: number;
}

/** `org.sync_run`: una fila por corrida del sync (qué hizo y qué higiene encontró). */
export interface OrgSyncRunRow {
  id: number;
  source: string;
  period: string;
  started_at: string;
  finished_at: string | null;
  ok: boolean | null;
  stats: OrgSyncStats | null;
  issues: OrgHygieneIssue[] | null;
}

/** `org.tool`: catálogo de herramientas (T5; nace de las ~60 que nombraron los líderes en B0). */
export interface OrgToolRow {
  slug: string;
  name: string;
  category: string | null;
  link: string | null;
  owner_area: string | null;
  owner_email: string | null;
  modality: OrgToolModality | null;
  roles: string[];
  per_client: boolean;
  seats_total: number | null;
  /** Costo mensual de la HERRAMIENTA (no de personas). */
  monthly_cost: number | null;
  paid_by: string | null;
  updated_at: string;
}

/** `org.policy`: solución + puesto (+ cliente opcional) → herramienta y rol requerido (T5). */
export interface OrgPolicyRow {
  id: number;
  solution_slug: string;
  role_title: string | null;
  client_id: string | null;
  tool_slug: string;
  tool_role: string | null;
  moment: OrgPolicyMoment;
  approver_email: string | null;
  created_by: string;
  created_at: string;
}

/** `org.effective_access`: lo que realmente pasó; referencia al sistema que lo administra (T5). */
export interface OrgEffectiveAccessRow {
  id: number;
  employee_id: number;
  tool_slug: string;
  client_id: string | null;
  status: OrgAccessStatus;
  granted_by: string | null;
  granted_at: string | null;
  revoked_at: string | null;
  /** 'vault' | 'cognito' | 'google-group' | 'external' */
  evidence_system: string | null;
  evidence_ref: string | null;
  updated_at: string;
}

/** `org.license`: asientos por herramienta y persona o equipo (T5). */
export interface OrgLicenseRow {
  id: number;
  tool_slug: string;
  employee_id: number | null;
  team: string | null;
  seat_type: string | null;
  monthly_cost: number | null;
  cost_center: string | null;
  valid_from: string | null;
  valid_to: string | null;
}

/** `org.schema_migration`: qué migraciones del DDL ya corrieron. */
export interface OrgSchemaMigrationRow {
  id: string;
  applied_at: string;
}

/**
 * Columnas de cada tabla, como las declaran las interfaces de arriba. `test:org` exige
 * que el DDL las tenga todas (y nada de `cost`/`salary` en persona ni asignación).
 */
export const ORG_TABLE_COLUMNS = {
  solution: ['slug', 'name', 'allocator_name', 'capacity_practice', 'community_areas', 'access_pattern', 'works_with_clients', 'source', 'owner_email', 'updated_at'],
  person: ['employee_id', 'email', 'full_name', 'role_title', 'level', 'area', 'team', 'solution_slug', 'people_manager_email', 'status', 'allocator_member_id', 'allocator_active', 'synced_at'],
  client: ['client_id', 'display_name', 'capacity_names', 'allocator_names', 'pod', 'lead_email', 'n_plus_one_email', 'growth_partner_email', 'synced_at'],
  client_lock: ['client_id', 'period', 'locked_by', 'source_system', 'synced_at'],
  client_alias_pending: ['source', 'name', 'first_seen', 'last_seen', 'persons', 'updated_at'],
  assignment: ['id', 'employee_id', 'client_id', 'solution_slug', 'period', 'role_title', 'pct', 'origin', 'status', 'manager_email', 'confirmed_by', 'confirmed_at', 'closed_at', 'closed_reason', 'source_system', 'source_ref', 'source_client_name', 'created_at', 'updated_at'],
  assignment_history: ['id', 'assignment_id', 'at', 'by', 'action', 'field', 'from_value', 'to_value'],
  sync_run: ['id', 'source', 'period', 'started_at', 'finished_at', 'ok', 'stats', 'issues'],
  tool: ['slug', 'name', 'category', 'link', 'owner_area', 'owner_email', 'modality', 'roles', 'per_client', 'seats_total', 'monthly_cost', 'paid_by', 'updated_at'],
  policy: ['id', 'solution_slug', 'role_title', 'client_id', 'tool_slug', 'tool_role', 'moment', 'approver_email', 'created_by', 'created_at'],
  effective_access: ['id', 'employee_id', 'tool_slug', 'client_id', 'status', 'granted_by', 'granted_at', 'revoked_at', 'evidence_system', 'evidence_ref', 'updated_at'],
  license: ['id', 'tool_slug', 'employee_id', 'team', 'seat_type', 'monthly_cost', 'cost_center', 'valid_from', 'valid_to'],
  schema_migration: ['id', 'applied_at'],
} as const satisfies Record<string, readonly string[]>;

export type OrgTableName = keyof typeof ORG_TABLE_COLUMNS;

// ─── Agente de asignaciones (T3b) ────────────────────────────────────────────

/** Una propuesta ya resuelta contra el equipo y los clientes, y lo que pasó al escribirla. */
export interface OrgAgentProposal {
  assignmentId: string | null;
  employeeId: number;
  personName: string;
  personEmail: string;
  clientId: string;
  clientName: string;
  solutionSlug: string;
  pct: number | null;
  /** created = quedó sugerida para confirmar con el botón · exists = ya existía · forbidden = no es tu reporte directo */
  result: 'created' | 'exists' | 'forbidden';
}

export interface OrgAgentRejected {
  person: string;
  client: string;
  reason: 'person' | 'person_ambiguous' | 'self' | 'client' | 'solution' | 'pct';
}

export interface OrgAgentReply {
  reply: string;
  proposals: OrgAgentProposal[];
  rejected: OrgAgentRejected[];
}

// ─── T5: altas de catálogo, políticas y accesos ──────────────────────────────

export type OrgToolUpsert = Omit<OrgToolRow, 'updated_at'>;
export type OrgPolicyInput = Omit<OrgPolicyRow, 'id' | 'created_by' | 'created_at'>;
export type OrgEffectiveAccessUpsert = Pick<OrgEffectiveAccessRow, 'employee_id' | 'tool_slug' | 'client_id' | 'status' | 'evidence_system' | 'evidence_ref'>;
